import { Component, createMemo, For, createSignal, onMount } from 'solid-js';
import CountryFlag from '../ui/CountryFlag';
import DoughnutChart from '../charts/DoughnutChart';
import { useSocketEvent } from '../../services/socket';
import { conversionsApi } from '../../services/api';
import { getTodayGMT, isTodayGMT } from '../../utils/timezone';
import { TbWorld, TbChevronUp, TbChevronDown } from 'solid-icons/tb';
import type { Conversion } from '../../types';

interface CountryLead {
  country: string;
  count: number;
  leads?: number;
  payout?: number;
}

interface TopLeadsCountryProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

/**
 * Top Countries Component
 * Displays all countries with conversions for today (GMT+0)
 * Listens to real-time conversion events via Socket.io
 * Shows chart and detailed table of all countries ranked by conversion count
 */
const TopLeadsCountry: Component<TopLeadsCountryProps> = (props) => {
  const [conversions, setConversions] = createSignal<Conversion[]>([]);
  const [isLoading, setIsLoading] = createSignal(false);
  const [shouldAnimate, setShouldAnimate] = createSignal(false);

  onMount(async () => {
    // Load initial conversions for today from API
    setIsLoading(true);
    try {
      const today = getTodayGMT();

      const response = await conversionsApi.getAll({
        startDate: today,
        endDate: today,
        page: 1,
        limit: 1000
      });

      if (response.data) {
        setConversions(response.data);
      }
    } catch (error) {
      // Error loading conversions
    } finally {
      setIsLoading(false);
    }

    // Listen for real-time conversion events from backend
    // Backend emits 'conversion-recorded' event with conversion data
    useSocketEvent<Conversion>('conversion-recorded', (data) => {
      const dateStr = data.datetime || data.created_at || new Date().toISOString();
      const isToday = isTodayGMT(dateStr);

      // Add to conversions array
      setConversions((prev) => {
        // Only add if it's from today
        if (isToday) {
          const updated = [data, ...prev];
          return updated;
        }
        return prev;
      });
    });
  });

  // Calculate all countries with conversions for today (GMT+0)
  const topCountries = createMemo(() => {
    const countryMap = new Map<string, { count: number; leads: number; payout: number }>();
    const convs = conversions();

    // Count conversions per country for today (GMT+0) only
    convs.forEach((conversion) => {
      const dateStr = conversion.datetime || conversion.created_at;
      const isToday = dateStr ? isTodayGMT(dateStr) : false;

      if (isToday) {
        const country = conversion.country || 'Unknown';
        const existing = countryMap.get(country) || { count: 0, leads: 0, payout: 0 };

        countryMap.set(country, {
          count: existing.count + 1,
          leads: existing.leads,
          payout: existing.payout + Number(conversion.payout || 0),
        });
      }
    });

    // Convert to array and sort by count (descending)
    const result = Array.from(countryMap.entries())
      .map(([country, data]) => ({ country, ...data }))
      .sort((a, b) => b.count - a.count);

    return result;
  });

  // Color palette for chart (matching Tailwind colors)
  const colors = [
    'rgba(99, 102, 241, 0.8)',    // indigo
    'rgba(16, 185, 129, 0.8)',    // emerald
    'rgba(251, 191, 36, 0.8)',    // amber
    'rgba(239, 68, 68, 0.8)',     // red
    'rgba(139, 92, 246, 0.8)',    // violet
    'rgba(236, 72, 153, 0.8)',    // pink
    'rgba(59, 130, 246, 0.8)',    // blue
    'rgba(34, 197, 94, 0.8)',     // green
    'rgba(168, 85, 247, 0.8)',    // purple
    'rgba(249, 115, 22, 0.8)',    // orange
  ];

  // Prepare chart data with colors (cycle through colors for countries beyond the palette)
  const chartData = createMemo(() => {
    const countries = topCountries();
    const chartColors = countries.map((_, index) => colors[index % colors.length]);

    return {
      labels: countries.map((c) => c.country),
      datasets: [
        {
          label: 'Leads',
          data: countries.map((c) => c.count || 0),
        },
      ],
      colors: chartColors,
    };
  });

  return (
    <div class="flex flex-col h-full bg-body dark:bg-dark border border-dashed border-gray-200 dark:border-gray-700">
      {/* Header - Sticky */}
      <div class="sticky top-0 z-10 flex items-center justify-between p-4 md:p-6  border-dashed border-gray-200 dark:border-gray-700 bg-body dark:bg-dark">
        <h2 class="text-lg font-medium uppercase text-gray-900 dark:text-white flex items-center gap-2 flex-1 tracking-wider">
          <TbWorld size={20} class="text-orange-500" />
          <span>Top Countries</span>
        </h2>
        <button
          onClick={() => {
            setShouldAnimate(true);
            props.onToggle?.();
          }}
          class="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-orange-500 transition text-gray-500 dark:text-gray-400 flex-shrink-0"
          title={props.isOpen ? 'Collapse' : 'Expand'}
        >
          {props.isOpen ? <TbChevronUp size={20} /> : <TbChevronDown size={20} />}
        </button>
      </div>

    

      {/* Content - Scrollable with animation */}
      <div
        class={`flex-1 overflow-hidden ${
          shouldAnimate()
            ? props.isOpen
              ? 'animate-expand-panel-slow'
              : 'animate-collapse-panel-slow'
            : props.isOpen
              ? ''
              : 'hidden'
        }`}
      >
        {(props.isOpen || (shouldAnimate() && !props.isOpen)) && (
        <div class="overflow-y-auto p-4 md:p-4 realtime-scrollbar h-full">
          {isLoading() ? (
            <div class="py-12 text-center">
              <p class="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">
                Loading data...
              </p>
            </div>
          ) : topCountries().length === 0 ? (
            <div class="py-12 text-center">
              <p class="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">
                No today data
              </p>
              <p class="text-gray-400 dark:text-gray-500 text-xs">
                Waiting for conversions...
              </p>
            </div>
          ) : (
            <div class="space-y-4">
              {/* Doughnut Chart */}
              <div class="bg-white dark:bg-light-dark rounded-lg p-4 shadow-card">
                <DoughnutChart
                  labels={chartData().labels}
                  datasets={chartData().datasets}
                  colors={chartData().colors}
                  height={200}
                />
              </div>

              {/* Data Table */}
              <div class="space-y-2">
                {/* Header */}
                <div class="grid grid-cols-12 gap-2 px-2 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                  <div class="col-span-6">Country</div>
                  <div class="col-span-3 text-right">Leads</div>
                  <div class="col-span-3 text-right">Payout</div>
                </div>

                {/* Rows */}
                <For each={topCountries()}>
                  {(item) => (
                    <div class="grid grid-cols-12 gap-1 p-1 hover:bg-gray-50 dark:hover:bg-gray-900/30 rounded transition">
                      <div class="col-span-6 flex items-center gap-1">
                        <CountryFlag code={item.country} size={16} />
                        <span class="text-sm font-medium text-gray-900 dark:text-white uppercase truncate">
                          {item.country}
                        </span>
                      </div>
                      <div class="col-span-3 text-right text-sm font-semibold text-blue-500">
                        {item.count || 0}
                      </div>
                      <div class="col-span-3 text-right text-sm font-semibold text-green-500">
                        ${item.payout ? item.payout.toFixed(2) : '0.00'}
                      </div>
                    </div>
                  )}
                </For>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default TopLeadsCountry;
